'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { createCharacter, askQwen } from '@/lib/actions';
import { User, ArrowLeft, Sparkles, Save, Trash2, Wand2 } from 'lucide-react';
import Link from 'next/link';

export default function NewCharacterPage() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [brainDump, setBrainDump] = useState('');
  const [isParsing, setIsParsing] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    species: '',
    faction: '',
    role: '',
    background: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBrainDump = async () => {
    if (!brainDump.trim()) return;
    setIsParsing(true);
    
    try {
      const prompt = `
        Parse this messy brain dump into a JSON object for a character.
        The brain dump: "${brainDump}"
        Return ONLY JSON with these fields: name, species, faction, role, background.
        If a field is missing, use "Unknown".
      `;
      const response = await askQwen(prompt, "You are a helpful assistant that extracts character details into JSON.");
      
      // Try to extract JSON from the response (sometimes LLMs add markdown)
      const jsonStr = response.match(/\{[\s\S]*\}/)?.[0];
      if (jsonStr) {
        const parsed = JSON.parse(jsonStr);
        setFormData({
          name: parsed.name || '',
          species: parsed.species || '',
          faction: parsed.faction || '',
          role: parsed.role || '',
          background: parsed.background || ''
        });
      }
    } catch (err) {
      console.error("Failed to parse brain dump", err);
    } finally {
      setIsParsing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => data.append(key, value));

    try {
      const id = await createCharacter(data);
      router.push(`/characters/${id}`);
      router.refresh();
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <header className="p-6 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
          <ArrowLeft size={20} /> Back to Gallery
        </Link>
        <h1 className="text-xl font-serif font-bold italic text-purple-400">Create New Sentinel</h1>
        <div className="w-24" /> {/* Spacer */}
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full p-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Left Side: Brain Dump (Phase 2) */}
        <section className="space-y-6">
          <div className="flex items-center gap-3 text-purple-400">
             <Sparkles size={24} />
             <h2 className="text-2xl font-serif font-bold">The Oracle's Help</h2>
          </div>
          <p className="text-slate-400 text-sm leading-relaxed italic">
            "Type a messy brain-dump about your character below, and I'll help you structure it into the form!"
          </p>
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4 shadow-xl shadow-purple-900/10">
             <textarea 
               value={brainDump}
               onChange={(e) => setBrainDump(e.target.value)}
               placeholder="e.g., Lorii is the leader and fakes having wings with her hair. She lives in Zrada and is very secretive..."
               className="w-full h-48 bg-slate-950 border border-slate-800 rounded-xl p-4 focus:outline-none focus:ring-2 focus:ring-purple-500/50 text-slate-300 placeholder-slate-600 resize-none"
             />
             <button 
               onClick={handleBrainDump}
               disabled={isParsing || !brainDump.trim()}
               className="w-full bg-purple-600 hover:bg-purple-500 disabled:opacity-50 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-all"
             >
               {isParsing ? <div className="animate-spin h-5 w-5 border-2 border-white/30 border-t-white rounded-full" /> : <Wand2 size={20} />}
               {isParsing ? 'The Oracle is thinking...' : 'Structure with AI'}
             </button>
          </div>
        </section>

        {/* Right Side: The Form */}
        <form onSubmit={handleSubmit} className="space-y-8 bg-slate-900/50 border border-slate-800 p-8 rounded-3xl backdrop-blur-sm">
           <div className="space-y-4">
              <label className="block">
                <span className="text-slate-500 text-xs uppercase tracking-widest font-mono ml-1">Character Name</span>
                <input 
                  required
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 focus:ring-2 focus:ring-purple-500/50 outline-none"
                />
              </label>

              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-slate-500 text-xs uppercase tracking-widest font-mono ml-1">Species</span>
                  <input 
                    name="species"
                    value={formData.species}
                    onChange={handleChange}
                    className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 focus:ring-2 focus:ring-purple-500/50 outline-none"
                  />
                </label>
                <label className="block">
                  <span className="text-slate-500 text-xs uppercase tracking-widest font-mono ml-1">Faction</span>
                  <input 
                    name="faction"
                    value={formData.faction}
                    onChange={handleChange}
                    className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 focus:ring-2 focus:ring-purple-500/50 outline-none"
                  />
                </label>
              </div>

              <label className="block">
                <span className="text-slate-500 text-xs uppercase tracking-widest font-mono ml-1">Role / Title</span>
                <input 
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 focus:ring-2 focus:ring-purple-500/50 outline-none"
                />
              </label>

              <label className="block">
                <span className="text-slate-500 text-xs uppercase tracking-widest font-mono ml-1">Background & Lore</span>
                <textarea 
                  name="background"
                  value={formData.background}
                  onChange={handleChange}
                  rows={6}
                  className="mt-1 w-full bg-slate-950 border border-slate-800 rounded-xl p-4 focus:ring-2 focus:ring-purple-500/50 outline-none resize-none"
                />
              </label>
           </div>

           <div className="flex gap-4 pt-4">
              <button 
                type="submit"
                disabled={loading}
                className="flex-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold py-4 rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-emerald-900/20"
              >
                {loading ? 'Saving...' : <><Save size={20} /> Create Character</>}
              </button>
              <button 
                type="button"
                onClick={() => setFormData({ name: '', species: '', faction: '', role: '', background: '' })}
                className="p-4 bg-slate-800 hover:bg-slate-700 rounded-xl text-slate-400 transition-colors"
              >
                <Trash2 size={20} />
              </button>
           </div>
        </form>
      </main>
    </div>
  );
}
